import { useState } from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { BaseCodeEditor } from '../../CodeEditor/CodeEditor';
import { Flex } from '../../Flex';
import IconButton from '../../IconButton';
import SvgIcon from '../../SvgIcon';
import { ReactComponent as CopyIcon } from '../../../assets/copy.svg';
import { ReactComponent as VIcon } from '../../../assets/v.svg';

const COLLAPSE_THRESHOLD = 12;

type Props = {
  value: string;
  language?: string;
};

const Container = styled.div`
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.common.black};
`;

const Header = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 2px 6px 2px 12px;
  background: ${({ theme }) => theme.palette.common.black};
`;

const Meta = styled(Flex)`
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray4};
`;

const LanguageLabel = styled.span`
  font-family: 'Geist Mono', monospace;
  text-transform: lowercase;
`;

const EditorScroll = styled.div`
  overflow-x: auto;
`;

const Footer = styled.button`
  display: block;
  width: 100%;
  padding: 6px 12px;
  border: none;
  background: ${({ theme }) => theme.palette.common.black};
  color: ${({ theme }) => theme.palette.common.blue};
  font-size: 12px;
  text-align: left;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const CopyTooltipContent = ({ isCopied }: { isCopied: boolean }) => (
  <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
    {isCopied && (
      <SvgIcon width={12} strokeColor="green">
        <VIcon />
      </SvgIcon>
    )}
    {isCopied ? 'Copied!' : 'Copy'}
  </Flex>
);

const ChatCodeBlock = ({ value, language }: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const lines = value.split('\n');
  const lineCount = lines.length;
  const isCollapsible = lineCount > COLLAPSE_THRESHOLD;
  const hiddenLineCount = lineCount - COLLAPSE_THRESHOLD;

  const displayValue =
    isCollapsible && !expanded
      ? lines.slice(0, COLLAPSE_THRESHOLD).join('\n')
      : value;

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setIsCopied(true);
  };

  return (
    <Container>
      <Header>
        <Meta>
          {language && <LanguageLabel>{language}</LanguageLabel>}
          <span>
            {lineCount} {lineCount === 1 ? 'line' : 'lines'}
          </span>
        </Meta>
        <Tooltip title={<CopyTooltipContent isCopied={isCopied} />}>
          <IconButton
            onClick={handleCopy}
            onMouseLeave={() => setIsCopied(false)}
          >
            <SvgIcon width={14} strokeColor="white">
              <CopyIcon />
            </SvgIcon>
          </IconButton>
        </Tooltip>
      </Header>

      <EditorScroll>
        <BaseCodeEditor
          mode={language || 'text'}
          value={displayValue}
          fontSize={13}
          minLines={1}
          maxLines={Infinity}
          readOnly
          showPrintMargin={false}
          wrapEnabled={false}
        />
      </EditorScroll>

      {isCollapsible && (
        <Footer onClick={() => setExpanded((prev) => !prev)}>
          {expanded
            ? 'Show less'
            : `Show ${hiddenLineCount} more ${
                hiddenLineCount === 1 ? 'line' : 'lines'
              }`}
        </Footer>
      )}
    </Container>
  );
};

export default ChatCodeBlock;
